import type { TFunction } from "i18next";
import { DateTime } from "luxon";
import type {
  Dashboard2ColleagueDayStatus,
  Dashboard2DayBookingSummary,
  Dashboard2WeekDay,
  DayWorkStatus
} from "../types/dashboard2-work-status.types";
import {
  statusHeadline,
  statusFinishedTimeLine,
  statusPillPrimaryLine
} from "./dashboard2-status-display.utils";
import { colleagueDisplayName } from "./dashboard2-schedule.utils";

export const DASHBOARD2_WEEK_DAY_CARD_HEIGHT_PX = 308;
export const DASHBOARD2_WEEK_DAY_ROW_PADDING_Y = 1.75;
export const DASHBOARD2_WEEK_DAY_STATUS_ROW_HEIGHT_PX = 68;
export const DASHBOARD2_WEEK_DAY_ICON_RAIL_WIDTH_PX = 20;
export const DASHBOARD2_MAX_VISIBLE_DAY_BOOKING_PILLS = 2;

export type WeekDayBookingPill = {
  id: string;
  label: string;
};

export type WeekDayCardActionKind = "floor_plan" | "book_place" | "plan_day" | "plan_holiday";

export function isWeekDayPlanned(status: DayWorkStatus): boolean {
  return status.kind === "office" || status.kind === "mobile_working";
}

/** True when the calendar day is strictly before today (user-local). */
export function isWeekDayKeyInPast(dayKey: string, now: DateTime = DateTime.now()): boolean {
  return dayKey < now.toFormat("yyyy-MM-dd");
}

/** True when the calendar day is strictly after today (user-local). */
export function isWeekDayKeyInFuture(dayKey: string, now: DateTime = DateTime.now()): boolean {
  return dayKey > now.toFormat("yyyy-MM-dd");
}

/** Past unplanned days show status as static text; planned days open booking detail instead. */
export function isWeekDayStatusRowClickable(day: Dashboard2WeekDay): boolean {
  if (day.userStatus.kind === "public_holiday") {
    return false;
  }

  if (day.userStatus.kind === "unplanned" && isWeekDayKeyInPast(day.dayKey)) {
    return false;
  }

  return true;
}

/** Past planned days open schedule detail; today and future open the status sheet. */
export function weekDayStatusRowOpensBookingDetail(day: Dashboard2WeekDay): boolean {
  return Boolean(day.primarySchedule) && isWeekDayKeyInPast(day.dayKey);
}

/** Unplanned (and not past) days keep Plan this day and also get a floor-plan shortcut. */
export function isWeekDayFloorPlanShortcutVisible(day: Dashboard2WeekDay): boolean {
  if (day.userStatus.kind !== "unplanned") {
    return false;
  }

  return isWeekDayActionVisible(day);
}

/** Past unplanned days have no bottom action (status row is also non-interactive). */
export function isWeekDayActionVisible(day: Dashboard2WeekDay): boolean {
  const actionKind = weekDayCardActionKind(day.userStatus);
  if (!actionKind) {
    return false;
  }

  if (day.userStatus.kind === "unplanned" && isWeekDayKeyInPast(day.dayKey)) {
    return false;
  }

  return true;
}

/** Holiday and not-set days use a shorter card — less content, clearer affordance. */
export function isWeekDayCompactCard(status: DayWorkStatus): boolean {
  return status.kind === "unplanned" || status.kind === "public_holiday";
}

function bookingSummaryToPill(booking: Dashboard2DayBookingSummary): WeekDayBookingPill {
  return {
    id: booking.id,
    label: booking.primaryLine.trim() || booking.typeLabel
  };
}

export function deriveDayBookingPills(status: DayWorkStatus): WeekDayBookingPill[] {
  return (status.additionalBookings ?? []).map(bookingSummaryToPill);
}

export function splitVisibleBookingPills(pills: readonly WeekDayBookingPill[]): {
  visible: WeekDayBookingPill[];
  overflowCount: number;
} {
  if (pills.length <= DASHBOARD2_MAX_VISIBLE_DAY_BOOKING_PILLS) {
    return { visible: [...pills], overflowCount: 0 };
  }

  const visibleCount = DASHBOARD2_MAX_VISIBLE_DAY_BOOKING_PILLS - 1;

  return {
    visible: pills.slice(0, visibleCount),
    overflowCount: pills.length - visibleCount
  };
}

export function selectWeekDayColleague(
  userStatus: DayWorkStatus,
  colleagues: readonly Dashboard2ColleagueDayStatus[]
): Dashboard2ColleagueDayStatus | null {
  if (userStatus.kind === "unplanned" || userStatus.kind === "public_holiday") {
    return null;
  }

  const candidates =
    userStatus.kind === "office"
      ? colleagues.filter(
          colleague =>
            colleague.status.kind === "office" && colleague.status.label === userStatus.label
        )
      : colleagues.filter(colleague => colleague.status.kind === "office");

  if (candidates.length === 0) {
    return null;
  }

  return candidates.reduce((best, current) => {
    const bestFrequency = best.coLocationFrequency ?? 0;
    const currentFrequency = current.coLocationFrequency ?? 0;

    return currentFrequency > bestFrequency ? current : best;
  });
}

export function weekDayCardActionKind(status: DayWorkStatus): WeekDayCardActionKind | null {
  if (status.kind === "office") {
    return "floor_plan";
  }
  if (status.kind === "mobile_working") {
    return "book_place";
  }
  if (status.kind === "unplanned") {
    return "plan_day";
  }
  if (status.kind === "public_holiday") {
    return "plan_holiday";
  }

  return null;
}

export function weekDayStatusLines(
  status: DayWorkStatus,
  t: TFunction
): { primary: string; secondary?: string; inventory?: string; time?: string } {
  if (status.kind === "public_holiday") {
    return {
      primary: statusHeadline(status, t),
      secondary: t("_dashboard2_statusHoliday")
    };
  }

  if (status.kind === "unplanned") {
    return { primary: t("_dashboard2_statusNotSet") };
  }

  if (status.kind === "mobile_working") {
    return {
      primary: t("_dashboard2_statusMobileWorking"),
      time: status.isFinished ? statusFinishedTimeLine(status, t) : undefined
    };
  }

  return {
    primary: statusPillPrimaryLine(status, t),
    inventory: status.detail?.trim() || undefined,
    time: formatWeekDayStatusTimeLine(status, t)
  };
}

function formatWeekDayStatusTimeLine(status: DayWorkStatus, t: TFunction): string | undefined {
  if (status.isFinished) {
    return statusFinishedTimeLine(status, t);
  }

  return status.timeRange?.trim() || undefined;
}

export function weekDayColleaguePresenceLabel(
  colleague: Dashboard2ColleagueDayStatus,
  userStatus: DayWorkStatus,
  t: TFunction
): string {
  const location = colleague.status.label;

  if (userStatus.kind === "office") {
    return t("_dashboard2_weekColleagueAlsoAtLocation", { location });
  }

  return t("_dashboard2_weekColleagueAtLocation", { location });
}

export function weekDayColleagueAriaLabel(
  colleague: Dashboard2ColleagueDayStatus,
  userStatus: DayWorkStatus,
  t: (key: string, options?: { name?: string; location?: string }) => string
): string {
  const name = colleagueDisplayName({
    userId: colleague.colleagueId,
    firstName: colleague.firstName,
    surname: colleague.surname
  });
  const location = colleague.status.label;

  if (userStatus.kind === "office") {
    return t("_dashboard2_weekColleagueAlsoAt", { name, location });
  }

  return t("_dashboard2_weekColleagueAt", { name, location });
}
